import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import NotebookSEO from './NotebookSEO';
import LoginModal from './LoginModal';

/* ─── Global CSS ──────────────────────────────────────────────────── */
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=Syne:wght@600;700;800&display=swap');

    :root {
      --bg:        #f7f8fa;
      --bg2:       #ffffff;
      --border:    rgba(0,0,0,0.08);
      --border2:   rgba(0,0,0,0.12);
      --dim:       #8a9099;
      --body:      #3d4451;
      --ink:       #0f1117;
      --accent:    #0066ff;
      --accent-bg: rgba(0,102,255,0.06);
      --accent-bd: rgba(0,102,255,0.18);
      --input-bg:  #ffffff;
      --success:   #10b981;
      --error:     #ef4444;
    }

    @keyframes fadeUp  { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:none; } }
    @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
    @keyframes spin    { to { transform:rotate(360deg); } }

    .reveal {
      opacity: 0;
      transform: translateY(30px);
      transition: opacity 0.7s ease, transform 0.7s ease;
    }
    .reveal.visible {
      opacity: 1;
      transform: none;
    }
    .reveal-delay-1 { transition-delay: 0.08s; }
    .reveal-delay-2 { transition-delay: 0.18s; }
    .reveal-delay-3 { transition-delay: 0.28s; }
  `}</style>
);

/* ─── Intersection Observer hook ─────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const els = ref.current.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); } }),
      { threshold: 0.12 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ─── Logo ───────────────────────────────────────────────────────── */
const Logo = () => {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
      <img src="/logo/scifi_logo_transprent.png" alt="sciFi Logo" style={{ height: 42, width: 'auto' }} />
    </div>
  );
};

/* ─── Navbar ─────────────────────────────────────────────────────── */
function Nav() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 36px', height: 56,
      background: scrolled ? 'rgba(247,248,250,0.92)' : 'rgba(247,248,250,0.7)',
      backdropFilter: 'blur(16px)',
      borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
      transition: 'border-color 0.3s, background 0.3s',
    }}>
      <Logo />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button type="button" style={{
          fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, letterSpacing: '0.1em',
          textTransform: 'uppercase', padding: '8px 20px',
          background: 'transparent', color: 'var(--ink)', borderRadius: 3,
          border: '1px solid var(--border2)', cursor: 'pointer', transition: 'border-color 0.18s',
        }}
          onClick={() => navigate('/hackathon/leaderboard')}
        >Final Results</button>
        <button type="button" style={{
          fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, letterSpacing: '0.1em',
          textTransform: 'uppercase', padding: '8px 20px',
          background: 'var(--ink)', color: '#fff', borderRadius: 3,
          transition: 'opacity 0.18s', border: 'none', cursor: 'pointer'
        }}
          onClick={() => navigate('/')}
          onMouseEnter={e => e.target.style.opacity = '0.82'}
          onMouseLeave={e => e.target.style.opacity = '1'}
        >Back to Home</button>
      </div>
    </nav>
  );
}

/* ─── Hero ───────────────────────────────────────────────────────── */
function Hero({ stats, onLoginClick }) {
  return (
    <section style={{
      minHeight: '80vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '100px 24px 60px', position: 'relative', overflow: 'hidden',
      background: 'var(--bg)',
    }}>
      {/* Subtle dot grid */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.12) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
        maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)',
        opacity: 0.5,
      }} />

      {/* Blue radial glow */}
      <div style={{
        position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 700, height: 350, borderRadius: '50%',
        background: 'radial-gradient(ellipse, rgba(0,102,255,0.06) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        textAlign: 'center', width: '100%', maxWidth: 860, position: 'relative',
        animation: 'fadeUp 0.7s 0.2s ease both',
      }}>
        {/* Badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          border: '1px solid rgba(239,68,68,0.3)', borderRadius: 3,
          padding: '6px 16px', marginBottom: 24,
          background: 'rgba(239,68,68,0.08)',
        }}>
          <span style={{ fontSize: 18 }}>✅</span>
          <span style={{
            fontFamily: 'IBM Plex Mono,monospace', fontSize: 11,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: '#dc2626', fontWeight: 600
          }}>HACKATHON ENDED</span>
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: 'Syne,sans-serif', fontSize: 'clamp(36px,6vw,68px)',
          fontWeight: 800, color: 'var(--ink)', marginBottom: 16,
          letterSpacing: '-0.03em', lineHeight: 1.1
        }}>
          Vital Signs Anomaly<br />Detection Sprint
        </h1>

        {/* Description */}
        <p style={{
          fontFamily: 'IBM Plex Mono,monospace', fontSize: 15,
          color: 'var(--body)', marginBottom: 32, lineHeight: 1.7,
          maxWidth: 640, margin: '0 auto 32px'
        }}>
          This hackathon has concluded. Thank you to all 200+ participants who competed in detecting anomalies
          in ICU patient vitals. Winners will be announced on April 15, 2026.
        </p>

        {/* Stats */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 32, marginBottom: 40, flexWrap: 'wrap'
        }}>
          {[
            { icon: '👥', label: 'Participants', value: '200+' },
            { icon: '🏥', label: 'ICU Patients', value: '6,388' },
            { icon: '📊', label: 'Submissions', value: 'Closed' },
            { icon: '🏆', label: 'Winners Announced', value: 'April 15' },
          ].map((stat, i) => (
            <div key={i} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6
            }}>
              <span style={{ fontSize: 24 }}>{stat.icon}</span>
              <div style={{
                fontFamily: 'Syne,sans-serif', fontSize: 24,
                fontWeight: 700, color: 'var(--ink)'
              }}>{stat.value}</div>
              <div style={{
                fontFamily: 'IBM Plex Mono,monospace', fontSize: 10,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: 'var(--dim)'
              }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{
          display: 'inline-block',
          fontFamily: 'IBM Plex Mono,monospace', fontSize: 12,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          textDecoration: 'none', padding: '16px 42px',
          background: 'var(--dim)', color: '#fff', fontWeight: 600,
          borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          border: 'none', cursor: 'not-allowed', opacity: 0.6
        }}>Submissions Closed</div>
      </div>
    </section>
  );
}

/* ─── Problem Statement ──────────────────────────────────────────── */
function ProblemStatement() {
  const ref = useReveal();
  return (
    <section ref={ref} style={{
      padding: '100px 40px',
      background: 'var(--bg)',
      borderTop: '1px solid var(--border)'
    }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div className="reveal" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 36
        }}>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
          <span style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            fontWeight: 600
          }}>Problem Statement</span>
        </div>

        <h2 className="reveal" style={{
          fontFamily: 'Syne,sans-serif',
          fontSize: 'clamp(26px,4vw,44px)',
          fontWeight: 800,
          color: 'var(--ink)',
          marginBottom: 20,
          letterSpacing: '-0.02em'
        }}>
          Detect Anomalies in ICU Patient Vitals
        </h2>

        <p className="reveal reveal-delay-1" style={{
          fontFamily: 'IBM Plex Mono,monospace',
          fontSize: 15,
          color: 'var(--body)',
          lineHeight: 1.8,
          marginBottom: 40
        }}>
          Build a machine learning model that identifies abnormal patterns in real-time vital sign monitoring data from surgical ICU patients.
        </p>

        {/* The Task */}
        <div className="reveal reveal-delay-2" style={{
          padding: '32px',
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          marginBottom: 24
        }}>
          <h3 style={{
            fontFamily: 'Syne,sans-serif',
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--ink)',
            marginBottom: 16,
            letterSpacing: '-0.01em'
          }}>📋 Your Task</h3>
          <p style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 14,
            color: 'var(--body)',
            lineHeight: 1.8,
            marginBottom: 0
          }}>
            Given time-series vital signs data, predict an <strong style={{ color: 'var(--accent)' }}>anomaly_score</strong> (0 → 1) for each timestamp.
            Higher scores indicate more abnormal patterns. Your model will be evaluated on how well it identifies true anomalies in the test set.
          </p>
        </div>

        {/* Vital Signs */}
        <div className="reveal reveal-delay-3" style={{
          padding: '32px',
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          marginBottom: 24
        }}>
          <h3 style={{
            fontFamily: 'Syne,sans-serif',
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--ink)',
            marginBottom: 20,
            letterSpacing: '-0.01em'
          }}>🩺 Vital Signs Features</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              { name: 'HR', label: 'Heart Rate', range: '40–150 bpm', desc: 'Beats per minute' },
              { name: 'MBP', label: 'Mean Blood Pressure', range: '50–120 mmHg', desc: 'Average arterial pressure' },
              { name: 'SpO2', label: 'Oxygen Saturation', range: '90–100%', desc: 'Blood oxygen level' },
              { name: 'Temp', label: 'Temperature', range: '35.0–39.5°C', desc: 'Body temperature' },
            ].map((vital, i) => (
              <div key={i} style={{
                padding: '16px',
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 8
              }}>
                <div style={{
                  fontFamily: 'IBM Plex Mono,monospace',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--accent)',
                  marginBottom: 4
                }}>{vital.name}</div>
                <div style={{
                  fontFamily: 'Syne,sans-serif',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--ink)',
                  marginBottom: 6
                }}>{vital.label}</div>
                <div style={{
                  fontFamily: 'IBM Plex Mono,monospace',
                  fontSize: 11,
                  color: 'var(--dim)',
                  marginBottom: 4
                }}>{vital.desc}</div>
                <div style={{
                  fontFamily: 'IBM Plex Mono,monospace',
                  fontSize: 11,
                  color: 'var(--body)',
                  background: 'rgba(0,102,255,0.06)',
                  padding: '3px 8px',
                  borderRadius: 3,
                  display: 'inline-block',
                  marginTop: 4
                }}>Normal: {vital.range}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Data Structure */}
        <div className="reveal reveal-delay-3" style={{
          padding: '32px',
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          marginBottom: 24
        }}>
          <h3 style={{
            fontFamily: 'Syne,sans-serif',
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--ink)',
            marginBottom: 16,
            letterSpacing: '-0.01em'
          }}>📊 Data Structure</h3>

          <div style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 13,
            color: 'var(--body)',
            lineHeight: 1.8
          }}>
            <p style={{ marginBottom: 16 }}>
              <strong>train_vitals.csv</strong>  <br />
              <strong>test_vitals.csv</strong>
            </p>
            <div style={{
              background: 'var(--bg)',
              padding: '16px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              marginBottom: 16
            }}>
              <strong style={{ color: 'var(--accent)' }}>Columns:</strong><br />
              • <code style={{ color: 'var(--ink)' }}>case_id</code> — Unique patient identifier<br />
              • <code style={{ color: 'var(--ink)' }}>time_sec</code> — Timestamp in seconds<br />
              • <code style={{ color: 'var(--ink)' }}>HR</code>, <code style={{ color: 'var(--ink)' }}>MBP</code>, <code style={{ color: 'var(--ink)' }}>SpO2</code>, <code style={{ color: 'var(--ink)' }}>Temp</code> — Vital sign measurements<br />
              {/* • <code style={{ color: 'var(--ink)' }}>is_anomaly</code> — Ground truth label (train only) */}
            </div>
            <p style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 0 }}>
              ⚠️ Note: Data may contain missing values (sensor errors) and invalid readings (&lt; 0). Handle these appropriately.
            </p>
          </div>
        </div>

        {/* Output Format */}
        <div className="reveal reveal-delay-3" style={{
          padding: '32px',
          background: 'linear-gradient(135deg, rgba(0,102,255,0.04) 0%, rgba(0,102,255,0.01) 100%)',
          border: '1px solid var(--accent-bd)',
          borderRadius: 12
        }}>
          <h3 style={{
            fontFamily: 'Syne,sans-serif',
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--ink)',
            marginBottom: 16,
            letterSpacing: '-0.01em'
          }}>📤 Submission Format</h3>
          <div style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 13,
            color: 'var(--body)',
            lineHeight: 1.8
          }}>
            <p style={{ marginBottom: 12 }}>Submit a CSV with exactly 3 columns for every row in <code>test_vitals.csv</code>:</p>
            <div style={{
              background: 'var(--bg2)',
              padding: '16px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              fontFamily: 'monospace',
              fontSize: 12
            }}>
              case_id,time_sec,anomaly_score<br />
              1,0,0.23<br />
              1,1,0.45<br />
              1,2,0.89<br />
              ...
            </div>
            <p style={{ fontSize: 12, color: 'var(--dim)', marginTop: 12, marginBottom: 0 }}>
              • <strong>anomaly_score</strong> must be between 0 and 1<br />
              • Every row in test_vitals.csv must have a score<br />
              • Partial submissions will be rejected
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Data Source ────────────────────────────────────────────────── */
function DataSource() {
  const ref = useReveal();
  return (
    <section ref={ref} style={{
      padding: '80px 40px',
      background: 'linear-gradient(135deg, rgba(0,102,255,0.03) 0%, rgba(0,102,255,0.01) 100%)',
      borderTop: '1px solid var(--border)',
      borderBottom: '1px solid var(--border)'
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div className="reveal" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 24,
          justifyContent: 'center'
        }}>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
          <span style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            fontWeight: 600
          }}>Data Source</span>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
        </div>

        <div className="reveal reveal-delay-1" style={{
          textAlign: 'center',
          padding: '32px 40px',
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
        }}>
          <p style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 15,
            color: 'var(--body)',
            lineHeight: 1.9,
            marginBottom: 0
          }}>
            This hackathon uses <strong style={{ color: 'var(--ink)' }}>real intraoperative vital signs from VitalDB</strong> — a dataset of <strong style={{ color: 'var(--accent)' }}>6,388 surgical ICU patients</strong> from Seoul National University Hospital, South Korea. Data is fully anonymized and has been used in peer-reviewed research published in <strong style={{ color: 'var(--ink)' }}>Nature Scientific Data</strong> (Lee et al., 2022).
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── What You Get ───────────────────────────────────────────────── */
const FEATURES = [
  { icon: '📊', title: 'Real ICU Vitals Data', desc: 'VitalDB dataset with heart rate, BP, O2 saturation, and temperature from 6,388 surgical patients.' },
  { icon: '⚡', title: 'Free GPU Compute', desc: 'GPU-accelerated notebooks for training your anomaly detection models at no cost.' },
  { icon: '📓', title: 'sciFi Notebooks', desc: 'Interactive Python environment with pre-installed ML libraries, starter code, and visualization tools.' },
  { icon: '🤖', title: 'AI Coding Assistant', desc: 'Built-in AI copilot to help write, debug, and optimize your detection algorithms.' },
  { icon: '📈', title: 'Live Leaderboard', desc: 'Track your ranking in real-time after submissions open on April 4, 12:00 PM IST.' },
  { icon: '💬', title: 'Discord Community', desc: 'Get platform support, ask questions, and connect with participants on our Discord server.' },
];

function WhatYouGet() {
  const ref = useReveal();
  return (
    <section ref={ref} style={{ padding: '100px 40px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div className="reveal" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 36 }}>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
          <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--accent)' }}>WHAT YOU GET</span>
        </div>

        <h2 className="reveal" style={{
          fontFamily: 'Syne,sans-serif', fontSize: 'clamp(26px,4vw,44px)',
          fontWeight: 800, color: 'var(--ink)', marginBottom: 70,
          letterSpacing: '-0.02em'
        }}>
          Everything you need to build<br />winning ML models
        </h2>

        <div className="reveal reveal-delay-1" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))',
          gap: 12
        }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{
              padding: '30px 26px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              background: 'var(--bg)',
              transition: 'border-color 0.22s, box-shadow 0.22s, transform 0.22s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,102,255,0.2)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,102,255,0.06)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{ fontSize: 28, marginBottom: 14 }}>{f.icon}</div>
              <h3 style={{
                fontFamily: 'Syne,sans-serif', fontSize: 16,
                fontWeight: 700, color: 'var(--ink)', marginBottom: 9,
                letterSpacing: '-0.01em'
              }}>{f.title}</h3>
              <p style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--dim)' }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Event Details ──────────────────────────────────────────────── */
const TIMELINE = [
  { date: 'April 3', time: '10:00 AM', event: 'Hackathon Starts', desc: 'Access your starter notebook and begin building', completed: true },
  { date: 'April 3', time: '3:00 PM IST', event: 'Submissions Open', desc: 'Start submitting your predictions via sciFi platform', completed: true },
  { date: 'April 4', time: '12:00 PM IST', event: 'Leaderboard Goes Live', desc: 'Real-time rankings based on your submissions', completed: true },
  { date: 'April 8', time: '11:59 PM', event: 'Submission Deadline', desc: 'Final submission cutoff', completed: true },
  { date: 'April 15', time: 'TBA', event: 'Winners Announced', desc: 'Results and prize distribution', upcoming: true },
];

function EventDetails() {
  const ref = useReveal();
  return (
    <section ref={ref} style={{ padding: '100px 40px', background: 'var(--bg)' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div className="reveal" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 36 }}>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
          <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--accent)' }}>TIMELINE</span>
        </div>

        <h2 className="reveal" style={{
          fontFamily: 'Syne,sans-serif', fontSize: 'clamp(26px,4vw,44px)',
          fontWeight: 800, color: 'var(--ink)', marginBottom: 12,
          letterSpacing: '-0.02em'
        }}>
          Event Schedule
        </h2>
        <p className="reveal reveal-delay-1" style={{
          fontSize: 13, color: 'var(--dim)', marginBottom: 52
        }}>4 days of intense ML development</p>

        <div className="reveal reveal-delay-2" style={{
          border: '1px solid var(--border)', borderRadius: 8,
          background: 'var(--bg2)', overflow: 'hidden'
        }}>
          {TIMELINE.map((item, i) => (
            <div key={i} style={{
              padding: '24px 32px',
              borderBottom: i < TIMELINE.length - 1 ? '1px solid var(--border)' : 'none',
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 24,
              alignItems: 'flex-start',
              background: item.upcoming ? 'var(--accent-bg)' : 'transparent',
              opacity: item.completed ? 0.7 : 1
            }}>
              <div>
                <div style={{
                  fontFamily: 'IBM Plex Mono,monospace', fontSize: 11,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: item.upcoming ? 'var(--accent)' : item.completed ? 'var(--dim)' : 'var(--accent)',
                  fontWeight: 600, marginBottom: 4
                }}>{item.date}</div>
                <div style={{
                  fontFamily: 'IBM Plex Mono,monospace', fontSize: 10,
                  color: 'var(--dim)'
                }}>{item.time}</div>
              </div>
              <div>
                <h4 style={{
                  fontFamily: 'Syne,sans-serif', fontSize: 16,
                  fontWeight: 700, color: 'var(--ink)', marginBottom: 6,
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  {item.completed && <span style={{ fontSize: 14 }}>✓</span>}
                  {item.upcoming && <span style={{ fontSize: 14 }}>🏆</span>}
                  {item.event}
                </h4>
                <p style={{
                  fontSize: 12, color: 'var(--dim)', lineHeight: 1.7
                }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Prize Section ─────────────────────────────────────────────── */
function PrizeSection() {
  const ref = useReveal();
  return (
    <section ref={ref} style={{
      padding: '100px 40px',
      background: 'linear-gradient(135deg, rgba(0,102,255,0.02) 0%, rgba(147,51,234,0.02) 100%)',
      borderTop: '1px solid var(--border)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative glow */}
      <div style={{
        position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: 600, height: 300, borderRadius: '50%',
        background: 'radial-gradient(ellipse, rgba(0,102,255,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 1000, margin: '0 auto', position: 'relative' }}>
        <div className="reveal" style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 36, justifyContent: 'center'
        }}>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
          <span style={{
            fontFamily: 'IBM Plex Mono,monospace', fontSize: 10,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'var(--accent)', fontWeight: 600
          }}>PRIZES & RECOGNITION</span>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
        </div>

        <h2 className="reveal" style={{
          fontFamily: 'Syne,sans-serif', fontSize: 'clamp(26px,4vw,44px)',
          fontWeight: 800, color: 'var(--ink)', marginBottom: 16,
          letterSpacing: '-0.02em', textAlign: 'center'
        }}>
          What You'll Win
        </h2>

        <p className="reveal reveal-delay-1" style={{
          fontFamily: 'IBM Plex Mono,monospace', fontSize: 14,
          color: 'var(--body)', textAlign: 'center', marginBottom: 60,
          lineHeight: 1.7, maxWidth: 680, margin: '0 auto 60px'
        }}>
          Top performers get exclusive perks and recognition in the sciFi community
        </p>

        {/* Top 3 Winners */}
        <div className="reveal reveal-delay-2" style={{
          padding: '40px 36px',
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          marginBottom: 32,
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
            <h3 style={{
              fontFamily: 'Syne,sans-serif', fontSize: 24,
              fontWeight: 800, color: 'var(--ink)', marginBottom: 8,
              letterSpacing: '-0.02em'
            }}>Top 3 Winners Receive</h3>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 20
          }}>
            <div style={{
              padding: '24px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🎖️</div>
              <h4 style={{
                fontFamily: 'Syne,sans-serif', fontSize: 16,
                fontWeight: 700, color: 'var(--ink)', marginBottom: 8
              }}>Founding User Status</h4>
              <p style={{
                fontFamily: 'IBM Plex Mono,monospace', fontSize: 12,
                color: 'var(--dim)', lineHeight: 1.7
              }}>
                Exclusive badge and early access to new features
              </p>
            </div>

            <div style={{
              padding: '24px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>💳</div>
              <h4 style={{
                fontFamily: 'Syne,sans-serif', fontSize: 16,
                fontWeight: 700, color: 'var(--ink)', marginBottom: 8
              }}>sciFi Pro Credits</h4>
              <p style={{
                fontFamily: 'IBM Plex Mono,monospace', fontSize: 12,
                color: 'var(--dim)', lineHeight: 1.7
              }}>
                Redeemable when Pro tier launches
              </p>
            </div>

            <div style={{
              padding: '24px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📣</div>
              <h4 style={{
                fontFamily: 'Syne,sans-serif', fontSize: 16,
                fontWeight: 700, color: 'var(--ink)', marginBottom: 8
              }}>Featured Recognition</h4>
              <p style={{
                fontFamily: 'IBM Plex Mono,monospace', fontSize: 12,
                color: 'var(--dim)', lineHeight: 1.7
              }}>
                Spotlight on leaderboard & social media
              </p>
            </div>
          </div>
        </div>

        {/* All Participants */}
        <div className="reveal reveal-delay-3" style={{
          padding: '32px 36px',
          background: 'var(--bg2)',
          border: '1px solid var(--accent-bd)',
          borderRadius: 12,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📜</div>
          <h4 style={{
            fontFamily: 'Syne,sans-serif', fontSize: 18,
            fontWeight: 700, color: 'var(--ink)', marginBottom: 12,
            letterSpacing: '-0.01em'
          }}>All Participants</h4>
          <p style={{
            fontFamily: 'IBM Plex Mono,monospace', fontSize: 13,
            color: 'var(--body)', lineHeight: 1.8, marginBottom: 0
          }}>
            Everyone who submits a valid solution receives a <strong style={{ color: 'var(--accent)' }}>Certificate of Participation</strong>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Sponsorship Section ────────────────────────────────────────── */
function Sponsorship() {
  const ref = useReveal();
  return (
    <section ref={ref} style={{
      padding: '100px 40px',
      background: 'var(--ink)',
      borderTop: '1px solid rgba(255,255,255,0.05)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle glow effect */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(0,102,255,0.15) 0%, transparent 70%)'
      }} />

      <div style={{ maxWidth: 860, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
        <div className="reveal" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          border: '1px solid rgba(0,102,255,0.3)', borderRadius: 2,
          padding: '4px 14px', marginBottom: 28,
          background: 'rgba(0,102,255,0.08)'
        }}>
          <span style={{
            fontSize: 9, fontFamily: 'IBM Plex Mono,monospace',
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'rgba(0,102,255,0.9)'
          }}>Partnership</span>
        </div>

        <h2 className="reveal reveal-delay-1" style={{
          fontFamily: 'Syne,sans-serif', fontSize: 'clamp(28px,5vw,48px)',
          fontWeight: 800, color: '#fff', marginBottom: 20,
          letterSpacing: '-0.02em', lineHeight: 1.2
        }}>
          AI in Cardiovascular Medicine<br />Partner With Us
        </h2>

        <p className="reveal reveal-delay-2" style={{
          fontFamily: 'IBM Plex Mono,monospace', fontSize: 15,
          color: 'rgba(255,255,255,0.6)', marginBottom: 36,
          lineHeight: 1.7, maxWidth: 640, margin: '0 auto 36px'
        }}>
          Join us as a sponsor and support the next generation of healthcare AI innovators.
        </p>

        <a
          href="mailto:contact@scifi.ink?subject=Hackathon Sponsorship Inquiry"
          className="reveal reveal-delay-3"
          style={{
            display: 'inline-block',
            fontFamily: 'IBM Plex Mono,monospace', fontSize: 12,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            textDecoration: 'none', padding: '16px 42px',
            background: '#fff', color: 'var(--ink)', fontWeight: 600,
            borderRadius: 3, boxShadow: '0 0 40px rgba(255,255,255,0.12)',
            transition: 'opacity 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={e => {
            e.target.style.opacity = '0.88';
            e.target.style.boxShadow = '0 0 64px rgba(255,255,255,0.22)';
          }}
          onMouseLeave={e => {
            e.target.style.opacity = '1';
            e.target.style.boxShadow = '0 0 40px rgba(255,255,255,0.12)';
          }}
        >Get in touch →</a>
      </div>
    </section>
  );
}

/* ─── Getting Started Section ────────────────────────────────────── */
function GettingStarted() {
  const ref = useReveal();
  return (
    <section ref={ref} style={{
      padding: '100px 40px',
      background: 'var(--bg2)',
      borderTop: '1px solid var(--border)'
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div className="reveal" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 36,
          justifyContent: 'center'
        }}>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
          <span style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            fontWeight: 600
          }}>Getting Started</span>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
        </div>

        <h2 className="reveal" style={{
          fontFamily: 'Syne,sans-serif',
          fontSize: 'clamp(26px,4vw,44px)',
          fontWeight: 800,
          color: 'var(--ink)',
          marginBottom: 50,
          textAlign: 'center',
          letterSpacing: '-0.02em'
        }}>
          Your Starter Pack
        </h2>

        {/* Video Tutorial */}
        <div className="reveal reveal-delay-1" style={{
          marginBottom: 40,
          padding: '32px',
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🎥</div>
            <h3 style={{
              fontFamily: 'Syne,sans-serif',
              fontSize: 20,
              fontWeight: 700,
              color: 'var(--ink)',
              marginBottom: 8,
              letterSpacing: '-0.01em'
            }}>Getting Started Tutorial</h3>
            <p style={{
              fontFamily: 'IBM Plex Mono,monospace',
              fontSize: 13,
              color: 'var(--dim)',
              lineHeight: 1.7
            }}>
              Watch this quick walkthrough to get started with the hackathon
            </p>
          </div>

          <div style={{
            position: 'relative',
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: '#000'
          }}>
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
              src="https://www.youtube.com/embed/8qgHPoN2q-0"
              title="Hackathon Starter Tutorial"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>

        <div className="reveal reveal-delay-2" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 20
        }}>
          {/* Starter Notebook */}
          <a
            href="https://scifi.ink/shared/169/vital-signs-hackathon"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '32px 28px',
              border: '1px solid var(--border)',
              borderRadius: 12,
              background: 'var(--bg)',
              textDecoration: 'none',
              transition: 'all 0.22s',
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(0,102,255,0.3)';
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,102,255,0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'none';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📓</div>
            <h3 style={{
              fontFamily: 'Syne,sans-serif',
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--ink)',
              marginBottom: 8,
              letterSpacing: '-0.01em'
            }}>Starter Notebook</h3>
            <p style={{
              fontSize: 13,
              lineHeight: 1.8,
              color: 'var(--dim)',
              marginBottom: 12
            }}>
              Pre-configured notebook with data loading, baseline models, and submission format examples.
            </p>
            <span style={{
              fontFamily: 'IBM Plex Mono,monospace',
              fontSize: 11,
              color: 'var(--accent)',
              fontWeight: 600,
              letterSpacing: '0.08em'
            }}>OPEN NOTEBOOK →</span>
          </a>

          {/* Discord Community */}
          <a
            href="https://discord.gg/HwXcAgvR6"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '32px 28px',
              border: '1px solid var(--border)',
              borderRadius: 12,
              background: 'var(--bg)',
              textDecoration: 'none',
              transition: 'all 0.22s',
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(0,102,255,0.3)';
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,102,255,0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'none';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
            <h3 style={{
              fontFamily: 'Syne,sans-serif',
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--ink)',
              marginBottom: 8,
              letterSpacing: '-0.01em'
            }}>Discord Community</h3>
            <p style={{
              fontSize: 13,
              lineHeight: 1.8,
              color: 'var(--dim)',
              marginBottom: 12
            }}>
              Get help, ask questions, and connect with other participants. Platform support available.
            </p>
            <span style={{
              fontFamily: 'IBM Plex Mono,monospace',
              fontSize: 11,
              color: 'var(--accent)',
              fontWeight: 600,
              letterSpacing: '0.08em'
            }}>JOIN DISCORD →</span>
          </a>
        </div>

        {/* Scoring Info */}
        <div className="reveal reveal-delay-3" style={{
          marginTop: 40,
          padding: '28px 32px',
          background: 'linear-gradient(135deg, rgba(0,102,255,0.04) 0%, rgba(0,102,255,0.01) 100%)',
          border: '1px solid var(--accent-bd)',
          borderRadius: 12
        }}>
          <h4 style={{
            fontFamily: 'Syne,sans-serif',
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--ink)',
            marginBottom: 16,
            letterSpacing: '-0.01em'
          }}>📊 Scoring Breakdown</h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 16,
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 12
          }}>
            {[
              { metric: 'ROC-AUC', weight: '40%' },
              { metric: 'PR-AUC', weight: '30%' },
              { metric: 'F1-Score', weight: '15%' },
              { metric: 'Recall', weight: '15%' }
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ color: 'var(--dim)', fontSize: 10, letterSpacing: '0.08em' }}>{item.metric}</span>
                <span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 16 }}>{item.weight}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Rules */}
        <div className="reveal reveal-delay-3" style={{
          marginTop: 24,
          padding: '24px 32px',
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 12
        }}>
          <h4 style={{
            fontFamily: 'Syne,sans-serif',
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--ink)',
            marginBottom: 12,
            letterSpacing: '-0.01em'
          }}>📋 Rules</h4>
          <ul style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 12,
            color: 'var(--body)',
            lineHeight: 1.9,
            paddingLeft: 20,
            margin: 0
          }}>
            <li>Solo participation only</li>
            <li>One submission per participant</li>
            <li>External datasets and pretrained models are allowed</li>
            <li>GPU compute available directly on sciFi</li>
            <li>Submission format: <code style={{ background: 'var(--bg)', padding: '2px 6px', borderRadius: 3 }}>case_id | time_sec | anomaly_score</code></li>
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ─── Submission Section ─────────────────────────────────────────── */
function SubmissionSection({ onLoginClick }) {
  const ref = useReveal();
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const isLoggedIn = !!localStorage.getItem('token');

  function handleFileChange(e) {
    const selected = e.target.files[0];
    if (selected && !selected.name.toLowerCase().endsWith('.csv')) {
      setError('Only CSV files are accepted.');
      setFile(null);
    } else {
      setError('');
      setFile(selected);
    }
  }

  async function handleSubmit() {
    if (!file) { setError('Please select a CSV file.'); return; }
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_BASE_URL}/api/hackathon/submit`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  function openModal() {
    setSuccess(false);
    setError('');
    setFile(null);
    setShowModal(true);
  }

  return (
    <>
      <section ref={ref} style={{
        padding: '100px 40px',
        background: 'var(--bg)',
        borderTop: '1px solid var(--border)'
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <div className="reveal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 36 }}>
            <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
            <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--accent)' }}>SUBMIT YOUR WORK</span>
          </div>

          <h2 className="reveal" style={{
            fontFamily: 'Syne,sans-serif', fontSize: 'clamp(26px,4vw,44px)',
            fontWeight: 800, color: 'var(--ink)', marginBottom: 16,
            letterSpacing: '-0.02em'
          }}>
            Submissions Closed
          </h2>

          <p className="reveal reveal-delay-1" style={{
            fontFamily: 'IBM Plex Mono,monospace', fontSize: 14,
            color: 'var(--body)', marginBottom: 40, lineHeight: 1.7
          }}>
            The submission deadline (April 8, 11:59 PM) has passed. Thank you to everyone who participated!
            Winners will be announced on <strong style={{ color: 'var(--accent)' }}>April 15, 2026</strong>.
          </p>

          <div
            className="reveal reveal-delay-2"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              fontFamily: 'IBM Plex Mono,monospace',
              fontSize: 12,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              padding: '16px 42px',
              background: 'var(--dim)',
              color: '#fff',
              fontWeight: 600,
              borderRadius: 3,
              border: 'none',
              cursor: 'not-allowed',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              opacity: 0.6
            }}
          >
            <span>Submissions Closed</span>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            animation: 'fadeIn 0.2s ease',
            padding: '20px'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: 'var(--bg2)',
              borderRadius: 12,
              padding: '40px',
              maxWidth: 500,
              width: '100%',
              border: '1px solid var(--border)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              animation: 'fadeUp 0.3s ease',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'transparent',
                border: 'none',
                fontSize: 24,
                color: 'var(--dim)',
                cursor: 'pointer',
                padding: 8,
                lineHeight: 1,
                transition: 'color 0.2s'
              }}
              onMouseEnter={e => e.target.style.color = 'var(--ink)'}
              onMouseLeave={e => e.target.style.color = 'var(--dim)'}
            >×</button>

            {success ? (
              /* ── Success state ── */
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 52, marginBottom: 20 }}>✅</div>
                <h3 style={{
                  fontFamily: 'Syne,sans-serif', fontSize: 26, fontWeight: 800,
                  color: 'var(--ink)', marginBottom: 14, letterSpacing: '-0.02em'
                }}>Submission Received!</h3>
                <p style={{
                  fontFamily: 'IBM Plex Mono,monospace', fontSize: 14,
                  color: 'var(--body)', lineHeight: 1.7, marginBottom: 32
                }}>
                  Your predictions have been uploaded successfully.<br />
                  We will evaluate your submission and inform you about the results.
                </p>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    width: '100%', padding: '14px',
                    fontFamily: 'IBM Plex Mono,monospace', fontSize: 11,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    background: 'var(--accent)', color: '#fff', border: 'none',
                    borderRadius: 6, cursor: 'pointer', fontWeight: 600, transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={e => e.target.style.opacity = '0.85'}
                  onMouseLeave={e => e.target.style.opacity = '1'}
                >Close</button>
              </div>
            ) : !isLoggedIn ? (
              /* ── Not logged in state ── */
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 20 }}>🔒</div>
                <h3 style={{
                  fontFamily: 'Syne,sans-serif', fontSize: 26, fontWeight: 800,
                  color: 'var(--ink)', marginBottom: 14, letterSpacing: '-0.02em'
                }}>Login Required</h3>
                <p style={{
                  fontFamily: 'IBM Plex Mono,monospace', fontSize: 14,
                  color: 'var(--body)', lineHeight: 1.7, marginBottom: 32
                }}>
                  You must be logged in to submit your predictions.
                </p>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); onLoginClick(); }}
                  style={{
                    width: '100%', padding: '14px',
                    fontFamily: 'IBM Plex Mono,monospace', fontSize: 11,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    background: 'var(--accent)', color: '#fff', border: 'none',
                    borderRadius: 6, cursor: 'pointer', fontWeight: 600,
                  }}
                >Login to Submit →</button>
              </div>
            ) : (
              /* ── Upload form ── */
              <>
                <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 20 }}>📤</div>
                <h3 style={{
                  fontFamily: 'Syne,sans-serif', fontSize: 26, fontWeight: 800,
                  color: 'var(--ink)', marginBottom: 10, textAlign: 'center',
                  letterSpacing: '-0.02em'
                }}>Submit Your Predictions</h3>
                <p style={{
                  fontFamily: 'IBM Plex Mono,monospace', fontSize: 13,
                  color: 'var(--body)', lineHeight: 1.6, textAlign: 'center', marginBottom: 28
                }}>
                  Upload a CSV: <code style={{ background: 'var(--bg)', padding: '2px 6px', borderRadius: 3 }}>case_id, time_sec, anomaly_score</code>
                </p>

                {/* File drop area */}
                <label style={{
                  display: 'block', border: '2px dashed var(--border2)',
                  borderRadius: 8, padding: '28px', textAlign: 'center',
                  cursor: 'pointer', marginBottom: 20, transition: 'border-color 0.2s, background 0.2s',
                  background: file ? 'var(--accent-bg)' : 'var(--bg)',
                  borderColor: file ? 'var(--accent)' : 'var(--border2)',
                }}>
                  <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
                  {file ? (
                    <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>
                      {file.name}
                    </span>
                  ) : (
                    <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 13, color: 'var(--dim)' }}>
                      Click to select your CSV file
                    </span>
                  )}
                </label>

                {error && (
                  <p style={{
                    fontFamily: 'IBM Plex Mono,monospace', fontSize: 12,
                    color: 'var(--error)', marginBottom: 16, textAlign: 'center'
                  }}>{error}</p>
                )}

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={uploading || !file}
                  style={{
                    width: '100%', padding: '14px',
                    fontFamily: 'IBM Plex Mono,monospace', fontSize: 11,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    background: uploading || !file ? 'var(--dim)' : 'var(--accent)',
                    color: '#fff', border: 'none', borderRadius: 6,
                    cursor: uploading || !file ? 'not-allowed' : 'pointer',
                    fontWeight: 600, transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={e => { if (!uploading && file) e.target.style.opacity = '0.85'; }}
                  onMouseLeave={e => { e.target.style.opacity = '1'; }}
                >
                  {uploading ? 'Uploading...' : 'Submit Predictions →'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Login to Participate ───────────────────────────────────────── */
function LoginToParticipate({ onLoginClick }) {
  const ref = useReveal();
  const [hackathonIdea, setHackathonIdea] = useState('');
  const [ideaSubmitted, setIdeaSubmitted] = useState(false);
  const [submittingIdea, setSubmittingIdea] = useState(false);

  const handleIdeaSubmit = async () => {
    if (!hackathonIdea.trim()) {
      alert('Please enter your hackathon idea');
      return;
    }

    setSubmittingIdea(true);
    try {
      await axios.post(`${API_BASE_URL}/api/hackathon/idea`, {
        idea: hackathonIdea
      });
      setIdeaSubmitted(true);
      setHackathonIdea('');
      setTimeout(() => setIdeaSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting idea:', error);
      alert('Failed to submit idea. Please try again.');
    } finally {
      setSubmittingIdea(false);
    }
  };

  return (
    <section id="register" ref={ref} style={{
      padding: '100px 40px', background: 'var(--bg2)',
      borderTop: '1px solid var(--border)'
    }}>
      <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <div className="reveal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 36 }}>
          <div style={{ width: 16, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
          <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--accent)' }}>JOIN THE SPRINT</span>
        </div>

        <h2 className="reveal" style={{
          fontFamily: 'Syne,sans-serif', fontSize: 'clamp(26px,4vw,44px)',
          fontWeight: 800, color: 'var(--ink)', marginBottom: 12,
          letterSpacing: '-0.02em'
        }}>
          Thank You for Participating
        </h2>
        <p className="reveal reveal-delay-1" style={{
          fontSize: 14, color: 'var(--body)', marginBottom: 8, lineHeight: 1.7
        }}>The hackathon has concluded. Check the leaderboard for final standings.</p>

        <div className="reveal reveal-delay-1" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 14px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 3,
          marginBottom: 40
        }}>
          <span style={{ fontSize: 14 }}>✅</span>
          <span style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 11,
            color: '#dc2626',
            fontWeight: 600,
            letterSpacing: '0.08em'
          }}>HACKATHON COMPLETED</span>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 20 }}>
          {/* View Leaderboard Button */}
          <button
            type="button"
            className="reveal reveal-delay-2"
            onClick={() => window.location.href = '/hackathon/leaderboard'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              fontFamily: 'IBM Plex Mono,monospace',
              fontSize: 12,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              padding: '16px 42px',
              background: 'var(--ink)',
              color: '#fff',
              fontWeight: 600,
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 28px rgba(0,0,0,0.18)';
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'none';
              e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.12)';
            }}
          >
            <span>View Final Leaderboard</span>
            <span>→</span>
          </button>

          {/* Share Feedback Button */}
          <button
            type="button"
            className="reveal reveal-delay-2"
            onClick={() => window.location.href = '/hackathon/feedback'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              fontFamily: 'IBM Plex Mono,monospace',
              fontSize: 12,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              padding: '16px 42px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              fontWeight: 600,
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 28px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'none';
              e.target.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.3)';
            }}
          >
            <span>Share Feedback</span>
            <span>★</span>
          </button>
        </div>

        {/* Next Hackathon Ideas */}
        <div className="reveal reveal-delay-4" style={{
          marginTop: 50,
          padding: '30px',
          background: 'rgba(0,102,255,0.03)',
          borderRadius: 8,
          border: '1px solid rgba(0,102,255,0.1)',
          maxWidth: 600,
          margin: '50px auto 30px'
        }}>
          <h3 style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--ink)',
            marginBottom: 12,
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}>
            What AI Hackathon You Want to See Next?
          </h3>
          <p style={{
            fontFamily: 'IBM Plex Mono,monospace',
            fontSize: 11,
            color: 'var(--dim)',
            marginBottom: 16,
            lineHeight: 1.6
          }}>
            Help us plan our next challenge! Share your ideas for future AI hackathon themes.
          </p>
          <textarea
            value={hackathonIdea}
            onChange={(e) => setHackathonIdea(e.target.value)}
            placeholder="e.g., NLP for medical records, Computer vision for agriculture, Time series forecasting..."
            disabled={submittingIdea || ideaSubmitted}
            style={{
              width: '100%',
              minHeight: 100,
              padding: 12,
              fontFamily: 'IBM Plex Mono,monospace',
              fontSize: 12,
              border: '1px solid var(--border)',
              borderRadius: 4,
              resize: 'vertical',
              background: ideaSubmitted ? '#f0fdf4' : 'white',
              color: 'var(--ink)'
            }}
          />
          <button
            onClick={handleIdeaSubmit}
            disabled={submittingIdea || ideaSubmitted}
            style={{
              marginTop: 12,
              padding: '10px 24px',
              fontFamily: 'IBM Plex Mono,monospace',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              background: ideaSubmitted ? '#10b981' : 'var(--accent)',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: submittingIdea || ideaSubmitted ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s',
              opacity: submittingIdea ? 0.6 : 1
            }}
            onMouseEnter={e => {
              if (!submittingIdea && !ideaSubmitted) e.target.style.opacity = '0.9'
            }}
            onMouseLeave={e => {
              if (!submittingIdea && !ideaSubmitted) e.target.style.opacity = '1'
            }}
          >
            {ideaSubmitted ? '✓ Submitted!' : submittingIdea ? 'Submitting...' : 'Submit Idea'}
          </button>
        </div>

        <p className="reveal reveal-delay-3" style={{
          fontFamily: 'IBM Plex Mono,monospace',
          fontSize: 12,
          color: 'var(--dim)',
          lineHeight: 1.7,
          marginTop: 30
        }}>
          Winners will be announced on April 15, 2026.
          <br />
          Thank you to all 200+ participants for making this event a success!
        </p>
      </div>
    </section>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function HackathonPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Fetch hackathon stats
    axios.get(`${API_BASE_URL}/api/hackathon/stats`)
      .then(res => setStats(res.data))
      .catch(err => console.error('Error fetching stats:', err));
  }, []);

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    navigate('/'); // Navigate to workspace after successful login
  };

  return (
    <>
      <NotebookSEO
        title="Vital Signs Anomaly Detection Sprint | Healthcare AI Hackathon"
        description="Detect anomalies in real ICU patient vitals from VitalDB. Free GPU compute, starter notebooks, and live leaderboard. Solo competition, April 3–8, 2026. Winners announced April 15."
        image="https://scifi.ink/hackathon_banner.avif"
      />
      <GlobalStyle />
      <Nav />
      <Hero stats={stats} onLoginClick={handleLoginClick} />
      <ProblemStatement />
      <WhatYouGet />
      <EventDetails />
      <PrizeSection />
      <DataSource />
      <GettingStarted />
      <SubmissionSection onLoginClick={handleLoginClick} />

      <Sponsorship />
      <LoginToParticipate onLoginClick={handleLoginClick} />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />
    </>
  );
}
